import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSpinner,
    faMagnifyingGlass,
    faCalendar,
    faRobot,
    faUser,
    faMessage,
    faChevronLeft,
    faChevronRight,
    faTimes,
    faThumbsUp,
    faAnglesLeft,
    faAnglesRight
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import apiHelper from '../../utils/apiHelper';
import './DaireUsage.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function DaireUsage({ currentUser }) {
    const [usageData, setUsageData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('all');
    const [selectedUser, setSelectedUser] = useState('all');
    const [selectedQuestionType, setSelectedQuestionType] = useState('all');
    const [selectedRating, setSelectedRating] = useState('all');
    const [sortConfig, setSortConfig] = useState({
        key: 'created',
        direction: 'desc'
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [statistics, setStatistics] = useState({
        uniqueUsers: 0,
        totalTokens: 0,
        totalInputTokens: 0,
        totalOutputTokens: 0,
        averageTokensPerConversation: 0
    });

    // Fetch usage data
    const fetchUsageData = async () => {
        setIsLoading(true);
        try {
            // Build query parameters
            const params = new URLSearchParams({
                page: currentPage,
                pageSize: itemsPerPage,
                sortKey: sortConfig.key,
                sortDirection: sortConfig.direction
            });

            // Add filters if they're not 'all'
            if (searchTerm) params.append('search', searchTerm);
            if (selectedMonth !== 'all') params.append('month', selectedMonth);
            if (selectedUser !== 'all') params.append('user', selectedUser);
            if (selectedQuestionType !== 'all') params.append('questionType', selectedQuestionType);
            if (selectedRating !== 'all') params.append('rating', selectedRating);

            const response = await apiHelper.get(`api/daire/usage?${params.toString()}`);
            if (response.data) {
                setUsageData(response.data.items);
                setTotalItems(response.data.totalItems);
                setTotalPages(response.data.totalPages);
                setStatistics(response.data.statistics);
            }
        } catch (error) {
            console.error('Error fetching usage data:', error);
            toast.error('Failed to fetch usage data');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle filter changes
    const handleFilterChange = (filterType, value) => {
        switch (filterType) {
            case 'search':
                setSearchTerm(value);
                break;
            case 'month':
                setSelectedMonth(value);
                break;
            case 'user':
                setSelectedUser(value);
                break;
            case 'questionType':
                setSelectedQuestionType(value);
                break;
            case 'rating':
                setSelectedRating(value);
                break;
            default:
                break;
        }
        setCurrentPage(1); // Reset to first page when filter changes
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    // Handle items per page change
    const handleItemsPerPageChange = (newSize) => {
        setItemsPerPage(newSize);
        setCurrentPage(1); // Reset to first page when changing page size
    };

    // Handle sorting
    const handleSort = (key) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key 
                ? current.direction === 'asc' ? 'desc' : 'asc'
                : 'asc'
        }));
    };

    // Get unique months from usage data
    const months = [...new Set(usageData.map(item => {
        const date = new Date(item.created);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }))].sort().reverse();

    // Get unique users from usage data
    const users = [...new Set(usageData.map(item => item.email).filter(Boolean))].sort();

    // Get unique question types from usage data
    const questionTypes = [...new Set(usageData.map(item => item.questionType || 'general').filter(Boolean))].sort();

    // Get unique ratings from usage data
    const ratings = ['all', 'positive', 'negative', 'neutral'];

    // Filter usage data
    const filteredData = usageData.filter(item => {
        const matchesSearch = (
            item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.title?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        const matchesMonth = selectedMonth === 'all' ? true : (() => {
            const itemDate = new Date(item.created);
            const itemMonth = `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}`;
            return itemMonth === selectedMonth;
        })();
        
        const matchesUser = selectedUser === 'all' ? true : item.email === selectedUser;
        
        const matchesQuestionType = selectedQuestionType === 'all' ? true : (item.questionType || 'general') === selectedQuestionType;
        
        const matchesRating = selectedRating === 'all' ? true : item.rating === selectedRating;
        
        return matchesSearch && matchesMonth && matchesUser && matchesQuestionType && matchesRating;
    });

    // Handle row click
    const handleRowClick = (item) => {
        setSelectedItem(item);
        setShowModal(true);
    };

    // Format date
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    useEffect(() => {
        fetchUsageData();
    }, [currentPage, itemsPerPage, searchTerm, selectedMonth, selectedUser, selectedQuestionType, selectedRating, sortConfig]);

    return (
        <div className="daire-usage">
            <div className="content-toolbar">
                <div className="toolbar-section search-section">
                    <div className="search-box">
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                        <input
                            type="text"
                            placeholder="Search by email or query..."
                            value={searchTerm}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                        />
                    </div>
                </div>

                <div className="toolbar-section filters-section">
                    <div className="filter-box">
                        <select
                            value={selectedMonth}
                            onChange={(e) => handleFilterChange('month', e.target.value)}
                        >
                            <option value="all">All Months</option>
                            {months.map(month => (
                                <option key={month} value={month}>
                                    {new Date(month).toLocaleDateString('en-GB', { year: 'numeric', month: 'long' })}
                                </option>
                            ))}
                        </select>
                        <FontAwesomeIcon icon={faCalendar} />
                    </div>

                    <div className="filter-box">
                        <select
                            value={selectedUser}
                            onChange={(e) => handleFilterChange('user', e.target.value)}
                        >
                            <option value="all">All Users</option>
                            {users.map(user => (
                                <option key={user} value={user}>
                                    {user}
                                </option>
                            ))}
                        </select>
                        <FontAwesomeIcon icon={faUser} />
                    </div>

                    <div className="filter-box">
                        <select
                            value={selectedQuestionType}
                            onChange={(e) => handleFilterChange('questionType', e.target.value)}
                        >
                            <option value="all">All Types</option>
                            {questionTypes.map(type => (
                                <option key={type} value={type}>
                                    {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                                </option>
                            ))}
                        </select>
                        <FontAwesomeIcon icon={faRobot} />
                    </div>

                    <div className="filter-box">
                        <select
                            value={selectedRating}
                            onChange={(e) => handleFilterChange('rating', e.target.value)}
                        >
                            {ratings.map(rating => (
                                <option key={rating} value={rating}>
                                    {rating.charAt(0).toUpperCase() + rating.slice(1)} Rating
                                </option>
                            ))}
                        </select>
                        <FontAwesomeIcon icon={faThumbsUp} />
                    </div>

                    <button 
                        className="refresh-btn"
                        onClick={fetchUsageData}
                        disabled={isLoading}
                    >
                        <FontAwesomeIcon 
                            icon={isLoading ? faSpinner : faRobot} 
                            spin={isLoading}
                        />
                        {isLoading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            <div className="usage-stats">
                <div className="stat-card">
                    <FontAwesomeIcon icon={faMessage} />
                    <div className="stat-content">
                        <h3>Total Conversations</h3>
                        <p>{totalItems.toLocaleString()}</p>
                    </div>
                </div>
                <div className="stat-card">
                    <FontAwesomeIcon icon={faUser} />
                    <div className="stat-content">
                        <h3>Unique Users</h3>
                        <p>{statistics.uniqueUsers.toLocaleString()}</p>
                    </div>
                </div>
                <div className="stat-card">
                    <FontAwesomeIcon icon={faRobot} />
                    <div className="stat-content">
                        <h3>Total Tokens</h3>
                        <p>{statistics.totalTokens.toLocaleString()}</p>
                    </div>
                </div>
            </div>

            <div className="usage-count">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} 
                of {totalItems} conversations
            </div>

            <div className="usage-table">
                <table>
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('created')} className="sortable">
                                Date <SortIndicator column="created" sortConfig={sortConfig} />
                            </th>
                            <th onClick={() => handleSort('email')} className="sortable">
                                User <SortIndicator column="email" sortConfig={sortConfig} />
                            </th>
                            <th onClick={() => handleSort('questionType')} className="sortable">
                                Type <SortIndicator column="questionType" sortConfig={sortConfig} />
                            </th>
                            <th onClick={() => handleSort('title')} className="sortable">
                                Query <SortIndicator column="title" sortConfig={sortConfig} />
                            </th>
                            <th onClick={() => handleSort('inputTokens')} className="sortable">
                                <Tippy content="Number of tokens in user's queries" placement="top">
                                    <span>Input <SortIndicator column="inputTokens" sortConfig={sortConfig} /></span>
                                </Tippy>
                            </th>
                            <th onClick={() => handleSort('outputTokens')} className="sortable">
                                <Tippy content="Number of tokens in Daire's responses" placement="top">
                                    <span>Output <SortIndicator column="outputTokens" sortConfig={sortConfig} /></span>
                                </Tippy>
                            </th>
                            <th onClick={() => handleSort('totalTokens')} className="sortable">
                                <Tippy content="Total tokens used (input + output)" placement="top">
                                    <span>Total <SortIndicator column="totalTokens" sortConfig={sortConfig} /></span>
                                </Tippy>
                            </th>
                            <th onClick={() => handleSort('rating')} className="sortable">
                                Rating <SortIndicator column="rating" sortConfig={sortConfig} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan="6" className="loading-cell">
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                    <span>Loading usage data...</span>
                                </td>
                            </tr>
                        ) : filteredData.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="empty-cell">
                                    No usage data found
                                </td>
                            </tr>
                        ) : (
                            filteredData.map((item, index) => (
                                <tr key={index} onClick={() => handleRowClick(item)} className="clickable-row">
                                    <td>{formatDate(item.created)}</td>
                                    <td>{item.email}</td>
                                    <td>{item.questionType ? item.questionType.charAt(0).toUpperCase() + item.questionType.slice(1).replace('_', ' ') : 'General'}</td>
                                    <td>
                                        <Tippy content={item.title} placement="top" delay={[300, 0]}>
                                            <div className="query-cell">
                                                {item.title}
                                            </div>
                                        </Tippy>
                                    </td>
                                    <td>{item.inputTokens?.toLocaleString() || 0}</td>
                                    <td>{item.outputTokens?.toLocaleString() || 0}</td>
                                    <td>{item.totalTokens?.toLocaleString() || 0}</td>
                                    <td>
                                        {item.rating ? (
                                            <span className={`rating-badge ${item.rating}`}>
                                                {item.rating === 'positive' ? '👍' : item.rating === 'negative' ? '👎' : '➖'}
                                            </span>
                                        ) : '—'}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="pagination-controls">
                <div className="items-per-page">
                    <span>Show:</span>
                    <select 
                        value={itemsPerPage} 
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        disabled={isLoading}
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                    <span>per page</span>
                </div>

                <div className="pagination-buttons">
                    <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1 || isLoading}
                        className="pagination-btn"
                    >
                        <FontAwesomeIcon icon={faAnglesLeft} />
                    </button>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || isLoading}
                        className="pagination-btn"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>

                    <span className="page-info">
                        Page {currentPage} of {totalPages}
                    </span>

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || isLoading}
                        className="pagination-btn"
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                    <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages || isLoading}
                        className="pagination-btn"
                    >
                        <FontAwesomeIcon icon={faAnglesRight} />
                    </button>
                </div>
            </div>

            {/* Usage Details Modal */}
            {showModal && selectedItem && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Usage Details</h2>
                            <button className="close-button" onClick={() => setShowModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-section">
                                <h3>User Information</h3>
                                <p><strong>Email:</strong> {selectedItem.email}</p>
                                <p><strong>Date:</strong> {formatDate(selectedItem.created)}</p>
                                <p><strong>Type:</strong> {selectedItem.questionType ? selectedItem.questionType.charAt(0).toUpperCase() + selectedItem.questionType.slice(1).replace('_', ' ') : 'General'}</p>
                            </div>
                            <div className="detail-section">
                                <h3>Query</h3>
                                <p>{selectedItem.title}</p>
                            </div>
                            <div className="detail-section">
                                <h3>Response</h3>
                                <div className="response-content">
                                    <ReactMarkdown 
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            p: ({node, ...props}) => <p className="response-paragraph" {...props} />,
                                            ul: ({node, ...props}) => <ul className="response-list" {...props} />,
                                            ol: ({node, ...props}) => <ol className="response-list" {...props} />,
                                            li: ({node, ...props}) => <li className="response-list-item" {...props} />,
                                            code: ({node, inline, ...props}) => 
                                                inline ? 
                                                    <code className="response-inline-code" {...props} /> : 
                                                    <pre className="response-code-block"><code {...props} /></pre>,
                                            blockquote: ({node, ...props}) => <blockquote className="response-quote" {...props} />,
                                            table: ({node, ...props}) => <div className="response-table-container"><table className="response-table" {...props} /></div>,
                                            th: ({node, ...props}) => <th className="response-table-header" {...props} />,
                                            td: ({node, ...props}) => <td className="response-table-cell" {...props} />
                                        }}
                                    >
                                        {selectedItem.response}
                                    </ReactMarkdown>
                                </div>
                            </div>
                            <div className="detail-section">
                                <h3>Token Usage</h3>
                                <div className="token-details">
                                    <div className="token-stat">
                                        <span>Input Tokens:</span>
                                        <span>{selectedItem.inputTokens?.toLocaleString() || 0}</span>
                                    </div>
                                    <div className="token-stat">
                                        <span>Output Tokens:</span>
                                        <span>{selectedItem.outputTokens?.toLocaleString() || 0}</span>
                                    </div>
                                    <div className="token-stat">
                                        <span>Total Tokens:</span>
                                        <span>{selectedItem.totalTokens?.toLocaleString() || 0}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="detail-section">
                                <h3>Rating</h3>
                                <div className="rating-display">
                                    {selectedItem.rating ? (
                                        <span className={`rating-badge ${selectedItem.rating}`}>
                                            {selectedItem.rating === 'positive' ? '👍 Positive' : 
                                             selectedItem.rating === 'negative' ? '👎 Negative' : 
                                             '➖ Neutral'}
                                        </span>
                                    ) : (
                                        <span className="rating-badge none">No rating</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Sort indicator component
const SortIndicator = ({ column, sortConfig }) => {
    if (sortConfig.key !== column) return null;
    return (
        <span className="sort-indicator">
            {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
        </span>
    );
};

DaireUsage.propTypes = {
    currentUser: PropTypes.object
};

export default DaireUsage; 